<template>
  <FormCard
    data-testid="self-service-form-linkedin-group-job-locations"
    data-group-key="job-locations"
    :title="jobLocationsTitle"
    :class="{ 'selected-card': openCard === `${jobLocationsTitle}` }"
    :is-open="openCard === `${jobLocationsTitle}`"
    @toggle="emit('toggle', `${jobLocationsTitle}`)"
    :completed="completedCards.has(`${jobLocationsTitle}`)"
    @enter-pressed="handleSaveAndContinue">
    <template #description>
      {{ selectedLocation?.name ?? $t('campaigns.linkedin.please_select_location') }}
    </template>
    <template #form-content>
      <div class="form-section" data-testid="self-service-field-location_id" data-field-key="location_id">
        <LeadCombobox
          v-model="selectedLocation"
          @search="onSearch"
          :getOptionValue="(option) => option.id"
          :getOptionLabel="(option) => option.name || ''"
          :items="locations"
          :loading="locationsLoading"
          :label="jobLocationsTitle"
          :disable-filtering="true" />
      </div>
      <div
        class="float-right"
        data-testid="self-service-save-action-job-locations"
        data-i18n-key="btn.save_and_continue">
        <LeadButton variant="secondary" :loading="loadingSave" @click="handleSaveAndContinue">
          {{ $t('btn.save_and_continue') }}
          <template #icon>
            <IconArrowBigRightFilled size="16" stroke-width="1.5" />
          </template>
        </LeadButton>
      </div>
    </template>
  </FormCard>
</template>

<script setup lang="ts">
import FormCard from '@/components/orders/form_cards/FormCard.vue';
import LeadButton from '../generic/LeadButton.vue';
import { IconArrowBigRightFilled } from '@tabler/icons-vue';
import { ref, watch, computed, type Ref } from 'vue';
import { updateCampaignObject } from '@/api/campaign';
import { useToast } from 'vue-toastification';
import type { FieldGroup } from '@/types/types';
import { debounce } from '@prospective/lithium';
import { request } from '@/api/client';
import translator from '@/locale/translator';
import { useProviderStore } from '@/stores/providers';
import LeadCombobox from './LeadCombobox.vue';
import { storeToRefs } from 'pinia';
import type { LinkedInForm } from '@/components/orders/details/provider_forms/LinkedInForm.vue';

const props = defineProps<{
  providerId: string | null | undefined;
  openCard: string | null;
  completedCards: Set<string>;
  object: any;
  fieldGroups: FieldGroup[];
}>();

const emit = defineEmits<{
  (e: 'toggle', cardKey: string): void;
  (e: 'completed', currentCardKey: string, nextCardKey: string | null): void;
  (e: 'update:object', obj: any): void;
}>();

const toast = useToast();
const providerStore = useProviderStore();
const locations = ref([]);
const locationsLoading = ref(false);
const selectedLocation = ref<{ id: string; name: string } | null>(null);
const loadingSave = ref(false);
const { obj } = storeToRefs(providerStore) as unknown as { obj: Ref<LinkedInForm> };

const searchLocations = async (search: string) => {
  if (!search) {
    locationsLoading.value = false;
    return [];
  }
  try {
    const res = await request('GET', `/v1/account/campaigns/${props.providerId}/options/linkedin_locations`, {
      query: search,
    });
    locations.value = res?.data || [];
  } finally {
    locationsLoading.value = false;
  }
};

const searchLocationsDebounced = debounce(searchLocations, 500);

// Flip the spinner immediately so it's visible during the debounce window too;
// cleared in searchLocations's finally once the fetch resolves.
function onSearch(search: string) {
  locationsLoading.value = true;
  searchLocationsDebounced(search);
}

const handleSaveAndContinue = async () => {
  if (selectedLocation.value) {
    try {
      loadingSave.value = true;
      const res = await updateCampaignObject(props.providerId ?? '', {
        location_id: selectedLocation.value.id,
        location_name: selectedLocation.value.name,
      });

      setTimeout(() => {
        loadingSave.value = false;
        providerStore.addCompletedCard(`${jobLocationsTitle.value}`, props.providerId ?? '');
        emit('completed', `${jobLocationsTitle.value}`, translator.get('campaigns.linkedin.terms_and_conditions'));
      }, 500);
      emit('update:object', res.data);
    } catch (error) {
      console.error('Error updating campaign object:', error);
      toast.error(translator.get('campaigns.linkedin.error_saving_location'));
      loadingSave.value = false;
    }
  }
};

const jobLocationsTitle = computed(() => {
  if (!props.object?.groups) return 'Job Locations';
  return props.object.groups[2]?.name || 'Job Locations';
});

watch(
  () => [props.object?.groups?.[2]?.properties?.[0]?.value, props.object?.groups?.[2]?.properties?.[1]?.value],
  ([locationName, locationId]) => {
    if (locationId && locationName && (!selectedLocation.value || selectedLocation.value.id !== locationId)) {
      selectedLocation.value = { id: locationId, name: locationName };
    }
  },
  { immediate: true }
);

watch(selectedLocation, (newVal) => {
  if (newVal) {
    obj.value.location_id = newVal.id;
    obj.value.location_name = newVal.name;
  }
});
</script>

<style scoped lang="scss">
.float-right {
  float: right;
}

.form-section {
  margin-bottom: 30px;
}
</style>
